<template>
  <div class="tabShareOpportunity">
    <h4 class="text-danger">Extensions</h4>
    <Accordion>
      <AccordionCard :isCollapsed="isCollapsed" :hideChevrons="true">
        <template slot="title">Select Extensions</template>
        <template slot="buttons">
          <button
            type="button"
            class="btn btn-md text-danger my-3"
            :disabled="isSaving"
            @click="() => showModal = true"
          >
            <font-awesome-icon class="mr-1" :icon="['fas', 'plus-circle']" />Add to Opportunity
          </button>
        </template>
        <TabExtensionTemplateSelect :templates="templates"/>
        <div class="mt-5">
          <strong>Select Existing Opportunities</strong>
          <div class="d-flex">
            <div
              v-for="selected in visibleSelectedOpportunities"
              class="p-3"
            >
              <span
                class="kt-badge kt-badge--brand kt-badge--lg badge-acronym"
                :class="`badge-acronym--${selected.acronym_brand.toLowerCase()}`"
              >
                {{ selected.acronym_brand }}
              </span>
              <span class="mx-3">{{ selected.opportunity_name }}</span>
              <button
                class="btn btn-outline-hover-danger kt-label-font-color-1"
                @click="toggleOpportunity(selected)">
                <font-awesome-icon :icon="['fas', 'trash-alt']" size="3x" />
              </button>
            </div>
          </div>
          <OpportunityListSearch
            ref="oplSearch"
            :isSearching="isSearching"
            @onSearch="onSearch"
            @onResetSearch="onSearch" />
          <OpportunityListTable
            :data="opportunitiesSaved"
            :meta="meta"
            :isSearching="isSearching"
            :searchParams="params"
            :show-edit="false"
            :show-duplicate="false"
            :show-delete="false"
            selectable
            :on-select="selectOpportunity"
          />
          <OpportunityListPagination
            v-if="Array.isArray(opportunitiesSaved) && opportunitiesSaved.length"
            ref="oplPagination"
            :total="total"
            :perPage="perPage"
            :currentPage="page"
            :maxPages="10"
            @onPage="onPage" />
        </div>
      </AccordionCard>
    </Accordion>
    <Portal to="extensionPortal">
      <ExtensionSelectorModal
        :show="showModal"
        :extensions="extensions"
        :related-opportunities="relatedOpportunities"
        :saving="isSaving"
        :save="save"
        :close-modal="() => showModal = false"
      />
    </Portal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import messageTypes from '@/store/messages/types'
import Accordion from '@/components/_shared/accordion/Accordion.vue'

import TabShareQuotePdf from '@/components/opportunity-edit/TabShareQuotePdf.vue'

import ModalMixin from '@/mixins/modal'
import AccordionCard from "@/components/_shared/accordion/AccordionCard.vue";
import TabExtensionTemplateSelect from "@/components/opportunity-edit/TabExtensionTemplateSelect.vue";
import OpportunityListTable from "@/components/opportunity-list/OpportunityListTable.vue";
import OpportunityListSearch from "@/components/opportunity-list/OpportunityListSearch.vue";
import OpportunityListPagination from "@/components/opportunity-list/OpportunityListPagination.vue";
import config from "@/config";
import ExtensionSelectorModal from "@/components/opportunity-edit/ExtensionSelectorModal.vue";
import NProgress from "nprogress";
import cloneDeep from "lodash.clonedeep";
import opportunityApi from "@/api/opportunity";

export default {
  name: 'TabExtensions',
  mixins: [ModalMixin],
  data () {
    return {
      templates: [],
      showModal: false,
      accordionCardSelected: '',
      isSaving: false,
      isEditing: false,
      isCollapsed: true,
      isCompleting: false,
      isSearching: false,
      meta: {
        title: 'Drafts',
        type: 'draft',
      },
      page: 0,
      params: {
        query: '',
        type: 5,
        limit: 5,
        offset: 0
      },
    }
  },
  components: {
    ExtensionSelectorModal,
    OpportunityListPagination,
    OpportunityListSearch,
    OpportunityListTable,
    TabExtensionTemplateSelect,
    AccordionCard,
    Accordion,
    TabShareQuotePdf,
  },
  props: {
    isCompletedQuote: {
      type: Boolean,
      default: false
    },
    data: {
      type: [Array, Object],
      required: false
    },
  },
  computed: {
    ...mapGetters({
      getMessage: 'messages/getMessage',
      opportunityData: 'opportunity/opportunity',
      opportunity: 'opportunity/getOpportunity',
      opportunities: 'opportunity/opportunitiesSaved',
      itinerarySourceTypeSelected: 'opportunity/itinerarySourceTypeSelected',
      opportunitySource: 'opportunity/opportunitySource',
    }),
    relatedOpportunities () {
      return this.visibleSelectedOpportunities.map((op) => {
        return {
          ...op,
          extensiontitle: op.opportunity_name,
          numberofdays: op.total_days,
          related_uuid: op.related_uuid,
        };
      });
    },
    extensions () {
      return this.opportunitySource.itineraries && this.opportunitySource.itineraries[this.itinerarySourceTypeSelected.index].extensions || []
    },
    visibleSelectedOpportunities () {
      return this.selectedOpportunities.filter(o => o.status !== -1);
    },
    selectedOpportunities () {
      return this.opportunityData.related_opportunities;
    },
    opportunitiesSaved () {
      if (this.opportunities && this.opportunities.items) {
        return this.opportunities.items
          .map((item) => {
            return {
              ...item,
              disabled: item.uuid === this.opportunityData.id,
              selected: this.visibleSelectedOpportunities.some(op => op.id === item.uuid),
            };
          })
      }
      return []
    },
    total () {
      if (this.opportunities && Number(this.opportunities.total) > 0) {
        return this.opportunities.total
      }
      return 0
    },
    perPage () {
      return config.opportunityList.pagination.byPage
    },
    currentPage () {
      return this.page
    },
    isLoadingCompleteQuote () {
      return this.getMessage(messageTypes.isLoadingCompleteQuote).value
    },
  },
  mounted() {
    this.search(this.params);
  },
  watch: {
    isCollapsed: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.templates = this.extensions.map((extension) => {
            return {
              id: extension.id,
              name: extension.extensiontitle,
              url: extension.extensionimage,
              active: extension.isselected,
            }
          });
        }
      }
    },
  },
  methods: {
    selectOpportunity (opportunity) {
      const opportunityFound = this.selectedOpportunities.find(
        (op) => op.uuid === opportunity.uuid
      );
      if (!opportunityFound) {
        this.selectedOpportunities.push({
          id: opportunity.uuid,
          uuid: opportunity.uuid,
          related_opportunity_id: opportunity.id,
          related_uuid: opportunity.uuid,
          stage: 1,
          url_image: "",
          opportunity_name: opportunity.opportunity_name,
          acronym_brand: opportunity.acronym_brand,
        });
      } else {
        this.toggleOpportunity(opportunity);
      }
    },
    toggleOpportunity (opportunity) {
      this.$store.dispatch(
        "opportunity/setOnlyOpportunity",
        {
          ...this.opportunity,
          related_opportunities: this.selectedOpportunities
            .map((op) => {
              if (op.id === opportunity.uuid) {
                op.status = op.status === -1 ? 1 : -1;
              }
              return op;
            })
        }
      );
    },
    onPage (page) {
      this.page = page
      this.params.offset = Number(this.params.limit) * Number(page)
      this.search(this.params)
    },
    search (params) {
      this.isSearching = true
      const payload = {
        query: params.query,
        type: 5,
        limit: 5,
        offset: params.offset
      }
      this.params = payload
      this.$store
        .dispatch('opportunity/listOpportunities', payload)
        .then(() => {
          this.isSearching = false
        })
    },
    onSearch (params) {
      this.page = 0
      params.offset = 0
      this.search(params)
    },
    onComplete () {
      this.onShowModal()
    },
    async saveExtensions(onComplete = () => {}) {
      this.isSaving = true

      const related_opportunities = [];
      for (const relatedOpportunity of this.opportunity.related_opportunities) {
        let total_days = 0;

        if (relatedOpportunity.status !== -1) {
          const { data } = await opportunityApi.getPublishedOpportunity(relatedOpportunity.uuid);
          const publishedOpportunity = data.data;
          const itinerary = publishedOpportunity.itineraries[0];
          total_days = itinerary.days.length;
        }

        related_opportunities.push({
          ...relatedOpportunity,
          total_days: total_days,
        });
      }

      await this.$store.dispatch(
        "opportunity/setOnlyOpportunity",
        {
          ...this.opportunity,
          related_opportunities: related_opportunities
        }
      );

      const clonedTemplates = cloneDeep(this.templates);
      this.extensions.forEach((extension) => {
        const template = clonedTemplates.find(t => t.id === extension.id);
        extension.isselected = template.active ? 1 : 0;
      });

      await this.save(onComplete);
    },
    async save(onComplete = () => {}) {
      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then(() => {
                this.isSaving = false
                this.showModal = false;
                NProgress.done()
                onComplete();
              });
          } else {
            this.isSaving = false
            this.showModal = false;
            NProgress.done()
            onComplete();
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          this.showModal = false;
          NProgress.done()
          onComplete();
        })
    },
  }
}
</script>

<style lang="scss">
.alert.alert-warning-alpha {
  color: #856404;
  background-color: #fff3cd;
  border-color: #ffeeba;
  border-radius: 0px;
}

.badge-acronym {
  font-weight: 500;
  font-size: 1.4rem;
}
.badge-acronym--iv {
  background-color: #091F40 !important;
}
.badge-acronym--cs {
  background-color: #0057A7 !important;
}
.badge-acronym--t {
  background-color: #ED1941 !important;
}
.badge-acronym--tc {
  background-color: #3182c5 !important;
}
.badge-acronym--lg {
  background-color: #9a844a !important;
}

</style>
